import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AutoCompleteLocationOption,
  AutoCompleteWithMobileModal,
} from '@reward-platform/lift/components'
import { AutoCompleteItemAttributes } from '@reward-platform/lift/components/AutoComplete/components/AutoComplete.types'
import { LocationOptionProps } from '@reward-platform/lift/components/AutoComplete/components/AutoCompleteLocationOption'
import { chakra, useMultiStyleConfig } from '@chakra-ui/react'
import useIntl from '~/hooks/useIntl/useIntl'
import useDebounce from '~/hooks/useDebounce/useDebounce'
import style from './SearchBox.module.css'

interface SearchBoxProps<T> {
  selectedItem: AutoCompleteItemAttributes | null | undefined
  onChange: (value: T) => void
  name: string
  label: string
  value: string
  error?: string
  partnerId: string
  searchFunction: (searchString: string) => Promise<T[]>
  getKey: (item: T) => string
  createAutoCompleteItem: (location: T) => LocationOptionProps
  onFieldCleared?: () => void
  extraQueryKeys?: string[]
  airportsList?: T[]
}

export const SearchBox = <T,>({
  partnerId,
  selectedItem,
  onChange,
  name,
  label,
  value,
  error,
  getKey,
  searchFunction,
  createAutoCompleteItem,
  onFieldCleared,
  extraQueryKeys,
  airportsList,
}: SearchBoxProps<T>) => {
  const styles = useMultiStyleConfig('SearchFlightForm')
  const intl = useIntl()
  const [searchString, setSearchString] = useState('')
  const [isViewingAirportsList, setIsViewingAirportsList] = useState(false)
  const debouncedSearchString = useDebounce(searchString, 500)

  const isAirportsListSupported = !!airportsList?.length
  const shouldShowAirportsList = isAirportsListSupported && debouncedSearchString === ''

  const querySameAsLabel = searchString === selectedItem?.label
  const canSearch =
    debouncedSearchString !== '' && debouncedSearchString === searchString && !querySameAsLabel

  const defaultQueryKey = ['SearchBox', name, partnerId, debouncedSearchString]
  const queryKey = extraQueryKeys ? [...defaultQueryKey, ...extraQueryKeys] : defaultQueryKey

  const { data: locations, isLoading } = useQuery({
    queryKey,
    queryFn: () => searchFunction(debouncedSearchString),
    enabled: canSearch,
    meta: {
      notifyUser: true,
      errorMessage: 'Flights Error - could not get flight locations',
    },
  })

  const noResultsFound = locations?.length === 0 && !isLoading && canSearch
  const airportInputRef = useRef<HTMLInputElement>(null)
  const suggestions =
    debouncedSearchString === '' && shouldShowAirportsList && isViewingAirportsList
      ? airportsList
      : locations
  const autocompleteItems = suggestions?.map(createAutoCompleteItem) ?? []

  const t = useMemo(
    () => ({
      searching: intl.formatMessage({
        id: 'search-box-searching',
        defaultMessage: 'Searching...',
      }),
      noResultsFound: intl.formatMessage({
        id: 'search-box-no-results-found',
        defaultMessage: 'No results found or not a valid route.',
      }),
      viewAllAirports: intl.formatMessage({
        id: 'view-all-airports',
        defaultMessage: 'View all airports',
      }),
    }),
    [intl]
  )

  return (
    <AutoCompleteWithMobileModal
      ref={airportInputRef}
      label={label}
      name={name}
      error={error}
      value={value}
      selectedItem={selectedItem}
      items={autocompleteItems}
      onInputValueChange={(v) => {
        const inputValue = v.inputValue ?? ''
        setSearchString(inputValue)

        if (inputValue === '') {
          onFieldCleared?.()
        }
      }}
      onSelectedItemChange={(v) => {
        const key = v.selectedItem?.value
        const location = suggestions?.find((l) => getKey(l) === key)

        if (location) {
          onChange(location)
        }

        setIsViewingAirportsList(false)
        airportInputRef.current?.blur()
      }}
    >
      {isLoading ? (
        <chakra.div __css={styles.searching}>{t.searching}</chakra.div>
      ) : (
        isAirportsListSupported &&
        !isViewingAirportsList &&
        debouncedSearchString === '' && (
          <chakra.div __css={styles.viewAllAirportsButtonContainer}>
            <button
              type="button"
              className={style.view_airports_list_button}
              onClick={() => setIsViewingAirportsList(true)}
            >
              {t.viewAllAirports}
            </button>
          </chakra.div>
        )
      )}

      {noResultsFound && <chakra.div __css={styles.destination}>{t.noResultsFound}</chakra.div>}
      {autocompleteItems.map((item, index) => (
        <AutoCompleteLocationOption key={item.value} item={item} index={index} />
      ))}
    </AutoCompleteWithMobileModal>
  )
}
